import AsyncFormInput from '@form/AsyncFormInput';
import { FC, Key, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ClubMember } from '../../../ClubMemberManagement/types';
import { useClubSlug } from '../../../Clubs/hooks';
import { Column } from '../../../DataTable';
import { CostCenter } from '../../../UserInvoice/types';

interface Props {
    spaceId?: Key;
    prefix: string;
    popupEnabled?: boolean;
    clubSlug?: string;
}

// `email_hidden` is set by the API when the club blurs its member list; number
// and email then arrive pre-masked and are for display only
type CompactMember = Pick<ClubMember, 'name' | 'email' | 'id' | 'number'> & { email_hidden?: boolean };

/**
 * Renders nothing and shuts the popup instead.
 *
 * The table only mounts this once it has loaded and found no rows, so a club
 * that exposes no member list never leaves an empty panel standing open.
 */
const ClosePopupWhenEmpty: FC<{ close: () => void }> = ({ close }) => {
    useEffect(() => close(), [close]);

    return null;
};

const BookerInput: FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    spaceId,
    prefix,
    popupEnabled,
    clubSlug: _clubSlug
}) => {
    const { t } = useTranslation('space-manager');
    const { setValue, getValues } = useFormContext();
    const clubSlug = useClubSlug(_clubSlug);

    // stores the picked member; a masked email is kept apart from `user_email`
    // so it never gets submitted — the server resolves the real one from member_id
    const handleSelect = useCallback(
        (member: CompactMember) => {
            const masked = Boolean(member.email_hidden);

            setValue(prefix + '.user_name', member.name);
            setValue(prefix + '.user_email', masked ? '' : (member.email ?? ''));
            setValue(prefix + '.email_masked', masked ? (member.email ?? '') : '');
            setValue(prefix + '.member_number', member.number);
            setValue(prefix + '.member_id', member.id);
            setValue(prefix + '.email_hidden', masked);
        },
        [prefix]
    );

    // Typing in the name field means the user is diverging from a previously picked
    // member, so detach it along with everything handleSelect derived from it. A stale
    // member_id would otherwise keep the email locked and make the server resolve
    // name/email from that member — silently booking under the wrong person.
    // setValue() from handleSelect dispatches no change event, so this only fires on
    // real user typing.
    const handleNameChange = useCallback(() => {
        if (!getValues(prefix + '.member_id')) return;

        setValue(prefix + '.member_id', undefined);
        setValue(prefix + '.member_number', undefined);
        setValue(prefix + '.user_email', '');
        setValue(prefix + '.email_masked', '');
        setValue(prefix + '.email_hidden', false);
    }, [prefix, getValues, setValue]);

    // number/email arrive pre-masked from the API (CompactMemberResource), so these
    // columns render whatever the server decided this viewer may see
    const columns: Column<CompactMember>[] = useMemo(
        () => [
            { uid: 'number', name: t('bookers.memberNumberColumn') },
            { uid: 'name', name: t('bookers.nameColumn') },
            { uid: 'email', name: t('bookers.emailColumn') }
        ],
        [t]
    );

    const keyResolver = useCallback((item: CostCenter) => String(item.id), []);

    // rendered by the table only after it loaded nothing — see ClosePopupWhenEmpty
    const renderEmpty = useCallback((close: () => void) => <ClosePopupWhenEmpty close={close} />, []);

    return (
        <AsyncFormInput
            enabled={popupEnabled}
            name={prefix + '.user_name'}
            inputProps={{ placeholder: t('bookers.namePlaceholder'), onChange: handleNameChange }}
            keyResolver={keyResolver}
            tableColumns={columns}
            fetchUrl={`club/${clubSlug}/space-manager/members`}
            onSelect={handleSelect}
            ListEmptyComponent={renderEmpty}
        />
    );
};

export default BookerInput;
